import { useCallback } from "react";

import { useDebounce } from "use-debounce";

import { useIsUsageEnabled } from "src/components/lineage/use-is-usage-enabled";
import {
  LineageStatus,
  type ResourceDependentCountsQuery,
  useResourceDependentCountsQuery,
} from "src/graphql";
import {
  type GovernedRefetchQuery,
  useGovernedRefetchInterval,
} from "src/hooks/use-governed-refetch-interval";

// Used for pages that contain filtering on table rows.
export const USAGE_COUNTS_DEBOUNCE_MS = 300;

export type DependentsCountResource = {
  resourceType: string;
  resourceId: string;
};

type UseDependentsCountsArgs = {
  resources: DependentsCountResource[];
  enabled?: boolean;
  debounceMs?: number;
};

export type DependentsCounts = {
  direct: number;
  indirect: number;
  total: number;
};

type UseDependentsCountsResult = {
  // Direct, indirect, and total dependent counts for a resource.
  countsFor: (resource: DependentsCountResource) => DependentsCounts | null;
  // Total dependents for a resource. Direct and indirect.
  totalFor: (resource: DependentsCountResource) => number | null;
  isLoading: boolean;
  isUnavailable: boolean;
};

function sameResources(
  left: DependentsCountResource[],
  right: DependentsCountResource[],
): boolean {
  return (
    left.length === right.length &&
    left.every(
      (resource, index) =>
        resource.resourceType === right[index]?.resourceType &&
        resource.resourceId === right[index]?.resourceId,
    )
  );
}

// Fetches dependent counts for several resources in one read.
export function useDependentsCounts({
  resources,
  enabled = true,
  debounceMs = 0,
}: UseDependentsCountsArgs): UseDependentsCountsResult {
  const isUsageEnabled = useIsUsageEnabled();
  const { refetchInterval: governedRefetchInterval } =
    useGovernedRefetchInterval<ResourceDependentCountsQuery>({
      baseMs: 5000,
      compareKey: (data) => [data?.resourceDependentCounts.status],
    });
  const rebuildingRefetchInterval = useCallback(
    (
      data: ResourceDependentCountsQuery | undefined,
      query?: GovernedRefetchQuery,
    ) =>
      data?.resourceDependentCounts.status === LineageStatus.Rebuilding
        ? governedRefetchInterval(data, query)
        : false,
    [governedRefetchInterval],
  );

  const [debouncedResources] = useDebounce(resources, debounceMs, {
    equalityFn: sameResources,
  });
  const isSettled = sameResources(debouncedResources, resources);

  const isEnabled = enabled && isUsageEnabled && resources.length > 0;
  const counts = useResourceDependentCountsQuery(
    { resources: debouncedResources },
    {
      enabled: isEnabled && isSettled,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: rebuildingRefetchInterval,
    },
  );

  const result = counts.data?.resourceDependentCounts;
  // Group counts by resource type and ID for lookup.
  const countsByType = new Map<string, Map<string, DependentsCounts>>();
  for (const count of result?.counts ?? []) {
    const counts =
      countsByType.get(count.resourceType) ??
      new Map<string, DependentsCounts>();
    counts.set(count.resourceId, {
      direct: count.directDependents,
      indirect: count.indirectDependents,
      total: count.directDependents + count.indirectDependents,
    });
    countsByType.set(count.resourceType, counts);
  }

  const countsFor = ({ resourceType, resourceId }: DependentsCountResource) =>
    countsByType.get(resourceType)?.get(resourceId) ?? null;

  const isUnavailable =
    Boolean(counts.error) || result?.status === LineageStatus.Failed;

  return {
    countsFor,
    totalFor: (resource) => countsFor(resource)?.total ?? null,
    isUnavailable,
    isLoading: isEnabled && !isUnavailable && (!isSettled || !result?.counts),
  };
}
